import { useEffect, useRef, useState } from 'react';
import { useParams, Link } from 'react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { ImportRunResponse, RunRecordResponse } from '@flixflow/shared';
import { importRunsApi } from '../api/importRuns.js';
import { getCsrfToken } from '../api/client.js';

type SseEvent =
  | { type: 'state'; data: { state: string } }
  | { type: 'progress'; data: { rowsRead: number; rowsOk: number; rowsSkipped: number; rowsError: number } }
  | { type: 'record'; data: { rowIndex: number; recordKey: string | null; status: string; message: string | null } }
  | { type: 'done'; data: { state: string; errorSummary: string | null; rowsRead: number; rowsOk: number; rowsSkipped: number; rowsError: number } };

type LiveRecord = {
  rowIndex: number;
  recordKey: string | null;
  status: string;
  message: string | null;
};

function RunStateBadge({ state }: { state: string }) {
  const map: Record<string, string> = {
    QUEUED: 'bg-neutral-100 text-neutral-600',
    RUNNING: 'bg-blue-100 text-blue-700',
    DONE: 'bg-green-100 text-green-700',
    ERROR: 'bg-red-100 text-red-700',
    CANCELLED: 'bg-neutral-100 text-neutral-500',
    INTERRUPTED: 'bg-yellow-100 text-yellow-700',
  };
  return (
    <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${map[state] ?? 'bg-neutral-100 text-neutral-600'}`}>
      {state}
    </span>
  );
}

function ProgressBar({ ok, skipped, error, total }: { ok: number; skipped: number; error: number; total: number }) {
  if (total === 0) return <div className="h-2 bg-neutral-100 rounded" />;
  const pOk = (ok / total) * 100;
  const pSkip = (skipped / total) * 100;
  const pErr = (error / total) * 100;
  return (
    <div className="h-2 rounded overflow-hidden bg-neutral-100 flex">
      <div style={{ width: `${pOk}%` }} className="bg-green-500" />
      <div style={{ width: `${pSkip}%` }} className="bg-neutral-300" />
      <div style={{ width: `${pErr}%` }} className="bg-red-400" />
    </div>
  );
}

export function ImportRunPage() {
  const { id: runId } = useParams<{ id: string }>();
  const qc = useQueryClient();

  const { data: run, isLoading } = useQuery({
    queryKey: ['import-run', runId],
    queryFn: () => importRunsApi.get(runId!),
    refetchInterval: (query) => {
      const state = query.state.data?.state;
      return state === 'RUNNING' || state === 'QUEUED' ? 3000 : false;
    },
  });

  const { data: records = [] } = useQuery({
    queryKey: ['import-run-records', runId],
    queryFn: () => importRunsApi.getRecords(runId!, 200, 0),
    enabled: !!run && run.state !== 'RUNNING' && run.state !== 'QUEUED',
  });

  const cancelMut = useMutation({
    mutationFn: () => importRunsApi.cancel(runId!),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['import-run', runId] }),
  });

  const [liveRecords, setLiveRecords] = useState<LiveRecord[]>([]);
  const [liveCounts, setLiveCounts] = useState<{ rowsRead: number; rowsOk: number; rowsSkipped: number; rowsError: number } | null>(null);
  const [sseState, setSseState] = useState<string | null>(null);
  const esRef = useRef<EventSource | null>(null);

  useEffect(() => {
    if (!runId || !run) return;
    if (run.state !== 'RUNNING' && run.state !== 'QUEUED') return;

    const es = new EventSource(`/sse/import-runs/${runId}`, { withCredentials: true });
    esRef.current = es;

    es.onmessage = (ev) => {
      try {
        const event = JSON.parse(ev.data as string) as SseEvent;
        if (event.type === 'state') {
          setSseState(event.data.state);
        } else if (event.type === 'progress') {
          setLiveCounts(event.data);
        } else if (event.type === 'record') {
          setLiveRecords((prev) => {
            const next = [event.data, ...prev];
            return next.slice(0, 500);
          });
        } else if (event.type === 'done') {
          setSseState(event.data.state);
          setLiveCounts({ rowsRead: event.data.rowsRead, rowsOk: event.data.rowsOk, rowsSkipped: event.data.rowsSkipped, rowsError: event.data.rowsError });
          void qc.invalidateQueries({ queryKey: ['import-run', runId] });
          void qc.invalidateQueries({ queryKey: ['import-run-records', runId] });
          es.close();
        }
      } catch {
        // ignore malformed event
      }
    };

    es.onerror = () => {
      es.close();
    };

    return () => {
      es.close();
      esRef.current = null;
    };
  }, [runId, run?.state, qc]);

  const csrfToken = getCsrfToken();
  const isActive = run?.state === 'RUNNING' || run?.state === 'QUEUED';

  const counts = liveCounts ?? (run ? { rowsRead: run.rowsRead, rowsOk: run.rowsOk, rowsSkipped: run.rowsSkipped, rowsError: run.rowsError } : null);
  const displayState = sseState ?? run?.state ?? '';
  const displayRecords: LiveRecord[] = isActive && liveRecords.length > 0
    ? liveRecords
    : records.map((r: RunRecordResponse) => ({ rowIndex: r.rowIndex, recordKey: r.recordKey, status: r.status, message: r.message }));

  if (isLoading) {
    return <div className="mx-auto max-w-4xl px-6 py-8 text-neutral-500 text-sm">Lade Run…</div>;
  }

  if (!run) {
    return (
      <div className="mx-auto max-w-4xl px-6 py-8">
        <p className="text-red-600 text-sm">Import-Run nicht gefunden.</p>
        <Link to="/importprofile" className="text-blue-600 text-sm hover:underline mt-2 inline-block">← Zurück zu Importprofilen</Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl px-6 py-8 space-y-6">
      <div className="flex items-center gap-3">
        <Link to={`/import-profile/${run.profileId}`} className="text-blue-600 text-sm hover:underline">
          ← Zurück zum Profil
        </Link>
      </div>

      <div className="bg-white border border-neutral-200 rounded-lg p-5 space-y-4">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-lg font-semibold">Import-Run</h2>
            <p className="text-xs text-neutral-400 font-mono mt-0.5">{run.id}</p>
          </div>
          <div className="flex items-center gap-3">
            <RunStateBadge state={displayState} />
            {isActive && (
              <button
                onClick={() => cancelMut.mutate()}
                disabled={cancelMut.isPending}
                className="px-3 py-1.5 text-xs border border-red-300 text-red-600 rounded hover:bg-red-50 disabled:opacity-60"
              >
                Abbrechen
              </button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
          <div>
            <p className="text-xs text-neutral-500 mb-0.5">Gestartet</p>
            <p className="font-medium">{new Date(run.startedAt).toLocaleString('de-DE')}</p>
          </div>
          {run.finishedAt && (
            <div>
              <p className="text-xs text-neutral-500 mb-0.5">Abgeschlossen</p>
              <p className="font-medium">{new Date(run.finishedAt).toLocaleString('de-DE')}</p>
            </div>
          )}
          {run.sourceFileName && (
            <div>
              <p className="text-xs text-neutral-500 mb-0.5">Datei</p>
              <p className="font-medium truncate">{run.sourceFileName}</p>
            </div>
          )}
          {run.sourceFileSize != null && (
            <div>
              <p className="text-xs text-neutral-500 mb-0.5">Dateigröße</p>
              <p className="font-medium">{(run.sourceFileSize / 1024).toFixed(1)} KB</p>
            </div>
          )}
        </div>

        {counts && (
          <div className="space-y-2">
            <ProgressBar ok={counts.rowsOk} skipped={counts.rowsSkipped} error={counts.rowsError} total={counts.rowsRead} />
            <div className="grid grid-cols-4 gap-3 text-center text-sm">
              <div className="bg-neutral-50 rounded p-2">
                <p className="text-xl font-semibold text-neutral-700">{counts.rowsRead}</p>
                <p className="text-xs text-neutral-500">Gelesen</p>
              </div>
              <div className="bg-green-50 rounded p-2">
                <p className="text-xl font-semibold text-green-700">{counts.rowsOk}</p>
                <p className="text-xs text-green-600">OK</p>
              </div>
              <div className="bg-neutral-50 rounded p-2">
                <p className="text-xl font-semibold text-neutral-500">{counts.rowsSkipped}</p>
                <p className="text-xs text-neutral-400">Übersprungen</p>
              </div>
              <div className="bg-red-50 rounded p-2">
                <p className="text-xl font-semibold text-red-600">{counts.rowsError}</p>
                <p className="text-xs text-red-500">Fehler</p>
              </div>
            </div>
          </div>
        )}

        {run.errorSummary && (
          <div className="bg-red-50 border border-red-200 rounded p-3">
            <p className="text-xs font-medium text-red-700 mb-1">Fehler-Zusammenfassung</p>
            <p className="text-xs text-red-600 font-mono whitespace-pre-wrap">{run.errorSummary}</p>
          </div>
        )}
      </div>

      {displayRecords.length > 0 && (
        <div className="bg-white border border-neutral-200 rounded-lg overflow-hidden">
          <div className="px-4 py-3 border-b border-neutral-100">
            <h3 className="text-sm font-medium">
              {isActive ? 'Live-Protokoll' : 'Datensatz-Protokoll'}
              {isActive && (
                <span className="ml-2 inline-flex items-center gap-1 text-xs text-blue-600">
                  <span className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse inline-block" />
                  live
                </span>
              )}
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-neutral-50 text-neutral-500">
                <tr>
                  <th className="px-3 py-2 text-left font-medium w-16">Zeile</th>
                  <th className="px-3 py-2 text-left font-medium">Schlüssel</th>
                  <th className="px-3 py-2 text-left font-medium w-24">Status</th>
                  <th className="px-3 py-2 text-left font-medium">Meldung</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-50">
                {displayRecords.map((rec, i) => (
                  <tr key={`${rec.rowIndex}-${i}`} className={rec.status === 'ERROR' ? 'bg-red-50' : rec.status === 'SKIPPED' ? 'bg-neutral-50/50' : ''}>
                    <td className="px-3 py-1.5 text-neutral-400 font-mono">{rec.rowIndex + 1}</td>
                    <td className="px-3 py-1.5 font-mono text-neutral-600">{rec.recordKey ?? '—'}</td>
                    <td className="px-3 py-1.5">
                      <span className={`inline-flex px-1.5 py-0.5 rounded text-xs font-medium ${
                        rec.status === 'OK' ? 'bg-green-100 text-green-700'
                        : rec.status === 'ERROR' ? 'bg-red-100 text-red-700'
                        : rec.status === 'SKIPPED' ? 'bg-neutral-100 text-neutral-500'
                        : 'bg-neutral-100 text-neutral-600'
                      }`}>
                        {rec.status}
                      </span>
                    </td>
                    <td className="px-3 py-1.5 text-neutral-500">{rec.message ?? ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!isActive && displayRecords.length === 0 && run.rowsRead === 0 && (
        <p className="text-center text-sm text-neutral-400 py-8">Keine Datensätze verarbeitet.</p>
      )}
    </div>
  );
}
